import {
  createStore,
  applyMiddleware
} from 'redux'
import thunk from 'redux-thunk'
import { ActionType } from './actions'
import Immutable from 'immutable'
import moment from 'moment'
import sieve from '@engrain/sieve'

// load up the config
let config = sightmapConfig(moment)

// set up defaults
let defaults = setFilterDefaults(config)

const initialState = Immutable.Map({
  isDocumentProcessing: false,
  config: config,
  document: null,
  documentErrors: null,
  documentBackgroundColor: '#292929',
  displayDetails: null,
  displayDocumentErrors: false,
  displayError: null,
  displayToolTip: null,
  documentFile: null,
  filters: Immutable.fromJS(defaults.filters),
  floorPicker: (config.layout === 'highrise'),
  highlightElement: null,
  highlightLevel: null,
  highlightHitArea: null,
  highlightUnit: null,
  isTouch: (('ontouchstart' in window) || window.DocumentTouch && document instanceof DocumentTouch),
  unitmap: null,
  units: null,
  filteredUnits: null,
  sieve: defaults.sieve
})

function reducer (state = initialState , action) {
  // console.log(action.type + ':', action, 'state:', state)

  switch (action.type) {
    case ActionType.SET_FLOOR_PICKER:
      return state.set('floorPicker', action.floorPicker)
    case ActionType.SET_SIGHT_MAP_CONFIG:
      return state.set('config', action.config)
    case ActionType.SET_UNIT_MAP:
      return state.set('unitmap', action.unitmap)
    case ActionType.SET_UNIT_MAP_BACKGROUND_COLOR:
      return state.set('documentBackgroundColor', action.color)
    case ActionType.HIGHLIGHT_ELEMENT:
      return state.set('highlightElement', action.element)
    case ActionType.HIGHLIGHT_LEVEL:
      return state.set('highlightLevel', action.level)
    case ActionType.HIGHLIGHT_HITAREA:
      return state.set('highlightHitArea', action.hitArea)
    case ActionType.HIGHLIGHT_UNIT:
      return state.set('highlightUnit', action.unit)
    case ActionType.DISPLAY_DOCUMENT_ERRORS:
      return state.set('displayDocumentErrors', action.display)
    case ActionType.DISPLAY_DETAILS:
      return state.set('displayDetails', action.unit)
    case ActionType.DISPLAY_ERROR:
      return state.set('displayError', action.message)
    case ActionType.DISPLAY_TOOL_TIP:
      state = state.set('highlightUnit', action.unit)
      if (action.unit !== null) {
        return state.set('displayToolTip', {
          unit: action.unit,
          x: action.x,
          y: action.y
        })
      } else {
        return state.set('displayToolTip', null)
      }
    case ActionType.RECEIVE_AND_APPLY_FILTER_CHANGE:
      let units = state.get('units').toJS()
      state = state.updateIn(['filters', action.valueFor], val => action.value)
      let sieve = state.get('sieve')
      let neededFilters = state.get('filters').toJS()
      for (let key in neededFilters) {
        let neededFilter = neededFilters[key]
        if (neededFilter === '') {
          delete neededFilters[key]
        }
      }
      let currentFilteredUnits = sieve.sift(units, neededFilters)

      // transform units array to object with ids as keys
      currentFilteredUnits = currentFilteredUnits.reduce(function (bin, value) {
        if (value.id !== undefined) {
          bin[value.id] = value
          return bin
        }
      }, {})

      return state.set('filteredUnits', Immutable.fromJS(currentFilteredUnits))
    case ActionType.REQUEST_FLOOR_PICKER:
      return state.merge({
        floorPicker: null,
        floorPickerFile: action.file
      })
    case ActionType.REQUEST_MAP_DOCUMENT:
      return state.merge({
        isDocumentProcessing: true,
        document: null,
        documentErrors: null,
        displayDocumentErrors: false,
        documentFile: action.file
      })
    case ActionType.REQUEST_UNITS:
      return state
    case ActionType.RECEIVE_FLOOR_PICKER:
      return state.merge({
        floorPicker: action.data
      })
    case ActionType.RECEIVE_MAP_DOCUMENT:
      return state.merge({
        isDocumentProcessing: false,
        document: action.data,
        displayDocumentErrors: action.errors.length > 0 ? true : false,
        documentErrors: action.errors
      })
    case ActionType.RECEIVE_UNITS:
      var units = action.data

      // Flatten unit and floorplan data:
      var loadedFloorplans = []
      units.forEach((unit) => {
        if (unit.links.floorplans[0] !== undefined) {
          let floorplan = unit.links.floorplans[0]
          if (loadedFloorplans.indexOf(floorplan.id) === -1) {
            // Load all floorplan images into memory for quick access
            if (floorplan.image !== '') {
              var image = new Image()
              image.src = floorplan.image
              loadedFloorplans.push(floorplan.id)
            }
          }
          for (var field in floorplan) {
            let value = floorplan[field]
            unit['floorplan_' + field] = value
          }
        }
      })

      // set the default floor with an available unit
      let defaultFloor = units.reduce(function (smallestFloor, unit) {
        let unitFloor = parseInt(unit.floor)
        return (unitFloor < smallestFloor && unitFloor && unit.available !== '1' ? unitFloor : smallestFloor)
      }, 99999)

      let unitQuery = {
        field: 'id',
        value: getParameterByName('unit_id')
      }
      if (unitQuery.value === '') {
        unitQuery = {
          field: 'unitnumber',
          value: getParameterByName('unit_number')
        }
      }

      let displayDetailsQuery
      switch (getParameterByName('display_details')) {
        case '0':
          displayDetailsQuery = false
          break
        case '1':
        case '':
        default:
          displayDetailsQuery = true
      }

      // if there is a unit and it's the initialState for units
      if (unitQuery.value !== null && unitQuery.value !== '' && state.toJS().units === null) {
        // transform all units array to object with query value as the keys:
        let unitsByField = units.reduce((bin, value) => {
          if (value[unitQuery.field] !== undefined) {
            bin[value[unitQuery.field]] = value
            return bin
          }
        }, {})

        let unit = unitsByField[unitQuery.value]

        if (unit !== undefined) {
          if ((unit.available === '2' || unit.available === '3') &&
            unit.is_not_available === '0') {
            // unit found and available
            state = state.set('highlightUnit', unit)
            state = state.set('displayDetails', displayDetailsQuery ? unit : null)

            // make sure the floor is a valid interger
            if (!isNaN(unit.floor) &&
              parseInt(Number(unit.floor)) == unit.floor &&
              !isNaN(parseInt(unit.floor, 10))) {
              defaultFloor = unit.floor
            }
          } else {
            // unit found but not available
            state = state.set('displayError', config.errors.unitFoundButNotAvailable)
          }
        } else {
          // unit not found at all
          state = state.set('displayError', config.errors.unitNotFound)
        }
      }

      if (defaultFloor !== 99999) {
        state = state.updateIn(['filters', 'floor'], val => defaultFloor.toString())
      }

      let filteredUnits = setDefaultFilteredUnits(state, units)

      return state.merge({
        units: units,
        filteredUnits: Immutable.fromJS(filteredUnits)
      })
    case ActionType.FILTER_RESET:
      state = state.set('filters', Immutable.fromJS(defaults.filters))

      var units = state.get('units').toJS()

      // set the default floor with an available unit
      var defaultFloor = units.reduce(function (smallestFloor, unit) {
        let unitFloor = parseInt(unit.floor)
        return ((unitFloor < smallestFloor && unitFloor && unit.available !== '1') ? unitFloor : smallestFloor)
      }, 99999)

      if (defaultFloor !== 99999) {
        state = state.updateIn(['filters', 'floor'], val => defaultFloor.toString())
      }

      let resetFilteredUnits = setDefaultFilteredUnits(state, units)

      return state.set('filteredUnits', Immutable.fromJS(resetFilteredUnits))
    default:
      return state
  }
}

function getParameterByName (name) {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
  var regex = new RegExp('[\\?&]' + name + '=([^&#]*)'),
    results = regex.exec(location.search)
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '))
}

function setFilterDefaults (config) {
  let defaults = {}

  // Set filters
  defaults.filters = {}
  config.inputs.forEach((input) => {
    if (input.options !== undefined && input.options[0] !== undefined) {
      defaults.filters[input.valueFor] = input.options[0].value
      if (input.defaultOption !== undefined) {
        defaults.filters[input.valueFor] = input.defaultOption
      } else if (defaults.filters[input.valueFor].indexOf('-') !== -1) { // if range
        let values = defaults.filters[input.valueFor].split('-')
        defaults.filters[input.valueFor] = {
          min: values[0],
          max: values[1]
        }
      }
    }
  })

  // config filters
  defaults.sieve = sieve()
  config.filters.forEach(function (filter, index) {
    if (filter.type !== undefined) {
      let name = 'filter' + index
      if (filter.field !== undefined) {
        name = filter.field
      }
      defaults.sieve.add(name, filter)
    }
  }.bind(this))

  return defaults
}

function setDefaultFilteredUnits (state, units) {
  let defaultFilters = state.get('filters').toJS()

  for (let key in defaultFilters) {
    let defaultFilter = defaultFilters[key]

    if (defaultFilter === '') {
      delete defaultFilters[key]
    }
  }
  let defaultFilteredUnits = state.get('sieve').sift(units, defaultFilters)

  // transform units array to object with ids as keys:
  return defaultFilteredUnits.reduce((bin, value) => {
    if (value.id !== undefined) {
      bin[value.id] = value
      return bin
    }
  }, {})
}

const createStoreWithMiddleware = applyMiddleware(
  thunk
)(createStore)

export default createStoreWithMiddleware(reducer)
